import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import { AIAnalytics, AIFeatureKeys } from '@Models/types';
import { useAuth } from './CognitoAuthProvider';
import { aiAnalyticsAPI } from '@Lib/axios/ai-analytics';
import showToast from '@Common/toast/showToast';

export type TimeFilter = '24h' | '7d' | '30d' | 'all';
export type ChartType = 'line' | 'bar' | 'pie';

export interface AnalyticsFilters {
  time: TimeFilter;
  user?: string;
  model?: string;
  feature?: AIFeatureKeys;
}

export interface AnalyticsSummary {
  totalTokens: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCost: number;
  totalInputCost?: number;
  totalOutputCost?: number;
  totalRequests: number;
}

export interface TimeSeriesData {
  timestamp: number;
  tokens: number;
  cost: number;
  requests: number;
}

export interface ModelUsageData {
  model: string;
  tokens: number;
  cost: number;
  requests: number;
  percentage: number;
}

export interface UserUsageData {
  username: string;
  tokens: number;
  cost: number;
  requests: number;
}

export interface FeatureUsageData {
  feature: AIFeatureKeys;
  tokens: number;
  cost: number;
  requests: number;
  percentage: number;
}

interface AnalyticsContextProps {
  rawData: AIAnalytics[];
  loading: boolean;
  error: string | null;
  filters: AnalyticsFilters;
  chartType: ChartType;

  summary: AnalyticsSummary;
  timeSeriesData: TimeSeriesData[];
  modelUsageData: ModelUsageData[];
  userUsageData: UserUsageData[];
  featureUsageData: FeatureUsageData[];

  availableUsers: string[];
  availableModels: string[];
  modelOptions: Record<string, string>;
  availableFeatures: AIFeatureKeys[];

  setFilters: (filters: Partial<AnalyticsFilters>) => void;
  setChartType: (type: ChartType) => void;
  refreshData: () => Promise<void>;
  exportData: () => void;
}

const AnalyticsContext = createContext<AnalyticsContextProps | undefined>(
  undefined
);

const TIME_FILTER_MS = {
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
} as const;

export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [rawData, setRawData] = useState<AIAnalytics[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<ChartType>('line');
  const [filters, setFiltersState] = useState<AnalyticsFilters>({
    time: '7d',
  });

  const { logout } = useAuth();
  const aiAnalytics = useMemo(() => aiAnalyticsAPI(logout), [logout]);

  const setFilters = (newFilters: Partial<AnalyticsFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }));
  };

  const filteredData = useMemo(() => {
    let filtered = rawData;

    if (filters.time !== 'all' && filters.time in TIME_FILTER_MS) {
      const timeThreshold = Date.now() - TIME_FILTER_MS[filters.time];
      filtered = filtered.filter((item) => item.timestamp >= timeThreshold);
    }

    if (filters.user) {
      filtered = filtered.filter((item) => item.username === filters.user);
    }

    if (filters.model) {
      filtered = filtered.filter(
        (item) => item.model.modelId === filters.model
      );
    }

    if (filters.feature) {
      filtered = filtered.filter((item) => item.featureKey === filters.feature);
    }

    return filtered;
  }, [rawData, filters]);

  const summary = useMemo((): AnalyticsSummary => {
    return {
      totalTokens: filteredData.reduce(
        (sum, item) => sum + item.tokens.total,
        0
      ),
      totalInputTokens: filteredData.reduce(
        (sum, item) => sum + item.tokens.input,
        0
      ),
      totalOutputTokens: filteredData.reduce(
        (sum, item) => sum + item.tokens.output,
        0
      ),
      totalCost: filteredData.reduce((sum, item) => sum + item.cost.total, 0),
      totalInputCost: filteredData.reduce(
        (sum, item) => sum + (item.cost?.input || 0),
        0
      ),
      totalOutputCost: filteredData.reduce(
        (sum, item) => sum + (item.cost?.output || 0),
        0
      ),
      totalRequests: filteredData.length,
    };
  }, [filteredData]);

  const timeSeriesData = useMemo((): TimeSeriesData[] => {
    const groupedByDate = new Map<string, TimeSeriesData>();

    filteredData.forEach((item) => {
      const date = new Date(item.timestamp);
      date.setHours(0, 0, 0, 0);
      const dateKey = date.getTime().toString();

      const existingData = groupedByDate.get(dateKey);
      if (existingData) {
        existingData.tokens += item.tokens.total;
        existingData.cost += item.cost.total;
        existingData.requests += 1;
      } else {
        groupedByDate.set(dateKey, {
          timestamp: date.getTime(),
          tokens: item.tokens.total,
          cost: item.cost.total,
          requests: 1,
        });
      }
    });

    return Array.from(groupedByDate.values()).sort(
      (a, b) => a.timestamp - b.timestamp
    );
  }, [filteredData]);

  const modelUsageData = useMemo((): ModelUsageData[] => {
    const modelMap = new Map<string, Omit<ModelUsageData, 'percentage'>>();

    filteredData.forEach((item) => {
      const modelKey = item.model.modelId;
      const existing = modelMap.get(modelKey);

      if (existing) {
        existing.tokens += item.tokens.total;
        existing.cost += item.cost.total;
        existing.requests += 1;
      } else {
        modelMap.set(modelKey, {
          model: item.model.name || modelKey,
          tokens: item.tokens.total,
          cost: item.cost.total,
          requests: 1,
        });
      }
    });

    const models = Array.from(modelMap.values());
    const totalTokens = models.reduce((sum, model) => sum + model.tokens, 0);

    return models
      .map((model) => ({
        ...model,
        percentage: totalTokens > 0 ? (model.tokens / totalTokens) * 100 : 0,
      }))
      .sort((a, b) => b.tokens - a.tokens);
  }, [filteredData]);

  const userUsageData = useMemo((): UserUsageData[] => {
    const userMap = new Map<string, UserUsageData>();

    filteredData.forEach((item) => {
      const username = item.username || 'Unknown';
      const existing = userMap.get(username);

      if (existing) {
        existing.tokens += item.tokens.total;
        existing.cost += item.cost.total;
        existing.requests += 1;
      } else {
        userMap.set(username, {
          username,
          tokens: item.tokens.total,
          cost: item.cost.total,
          requests: 1,
        });
      }
    });

    return Array.from(userMap.values()).sort((a, b) => b.tokens - a.tokens);
  }, [filteredData]);

  const featureUsageData = useMemo((): FeatureUsageData[] => {
    const featureMap = new Map<
      AIFeatureKeys,
      Omit<FeatureUsageData, 'percentage'>
    >();

    filteredData.forEach((item) => {
      const feature = item.featureKey;
      const existing = featureMap.get(feature);

      if (existing) {
        existing.tokens += item.tokens.total;
        existing.cost += item.cost.total;
        existing.requests += 1;
      } else {
        featureMap.set(feature, {
          feature,
          tokens: item.tokens.total,
          cost: item.cost.total,
          requests: 1,
        });
      }
    });

    const features = Array.from(featureMap.values());
    const totalTokens = features.reduce(
      (sum, feature) => sum + feature.tokens,
      0
    );

    return features
      .map((feature) => ({
        ...feature,
        percentage: totalTokens > 0 ? (feature.tokens / totalTokens) * 100 : 0,
      }))
      .sort((a, b) => b.tokens - a.tokens);
  }, [filteredData]);

  const availableUsers = useMemo(
    () =>
      Array.from(
        new Set(
          rawData
            .map((item) => item.username)
            .filter((username): username is string => Boolean(username))
        )
      ),
    [rawData]
  );

  const availableModels = useMemo(
    () => Array.from(new Set(rawData.map((item) => item.model.modelId))),
    [rawData]
  );

  const modelOptions = useMemo((): Record<string, string> => {
    const map: Record<string, string> = {};
    rawData.forEach((item) => {
      const id = item.model?.modelId;
      if (!id) return;
      map[id] = item.model?.name || id;
    });
    return map;
  }, [rawData]);

  const availableFeatures = useMemo(
    () => Array.from(new Set(rawData.map((item) => item.featureKey))),
    [rawData]
  );

  const refreshData = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await aiAnalytics.getAllAnalytics();
      setRawData(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch analytics data';
      setError(errorMessage);
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const exportData = () => {
    const dataToExport = {
      filters,
      summary,
      rawData: filteredData,
      timeSeriesData,
      modelUsageData,
      userUsageData,
      featureUsageData,
    };

    const dataStr = JSON.stringify(dataToExport, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `ai-analytics-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showToast.success('Analytics data exported successfully');
  };

  useEffect(() => {
    refreshData();
  }, []);

  const contextValue: AnalyticsContextProps = {
    rawData,
    loading,
    error,
    filters,
    chartType,
    summary,
    timeSeriesData,
    modelUsageData,
    userUsageData,
    featureUsageData,
    availableUsers,
    availableModels,
    modelOptions,
    availableFeatures,
    setFilters,
    setChartType,
    refreshData,
    exportData,
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): AnalyticsContextProps => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
